
import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { blogPosts } from '@/data/blogData';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import BlogSidebar from '@/components/blog/BlogSidebar';
import { Button } from '@/components/ui/button';
import NotFound from './NotFound';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(post => post.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  if (!post) {
    return <NotFound />;
  }

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Blog Post Header */}
      <section className="pt-32 pb-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center text-pkgreen-600 hover:text-pkgreen-700 mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to All Articles
            </Link>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-gray-900 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap gap-4 items-center text-gray-600">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{formatDate(post.date)}</span>
              </div>
              <div className="flex items-center">
                <User className="h-4 w-4 mr-2" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center">
                <Tag className="h-4 w-4 mr-2" />
                <span>{post.category}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Blog Post Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            <article className="w-full lg:w-2/3">
              <div className="mb-8 rounded-lg overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-auto object-cover"
                />
              </div>
              
              <div className="prose prose-lg max-w-none">
                {post.content.map((paragraph, index) => {
                  if (paragraph.type === 'heading') {
                    return <h2 key={index} className="text-2xl font-serif font-bold mt-8 mb-4">{paragraph.content}</h2>;
                  } else if (paragraph.type === 'subheading') {
                    return <h3 key={index} className="text-xl font-serif font-semibold mt-6 mb-3">{paragraph.content}</h3>;
                  } else if (paragraph.type === 'list') {
                    return (
                      <ul key={index} className="list-disc pl-6 my-4 space-y-2">
                        {paragraph.items.map((item, i) => (
                          <li key={i} className="text-gray-700">{item}</li>
                        ))}
                      </ul>
                    );
                  } else {
                    return <p key={index} className="text-gray-700 mb-6 leading-relaxed">{paragraph.content}</p>;
                  }
                })}
              </div>
              
              {/* Share Buttons */}
              <div className="border-t border-gray-200 pt-6 mt-8">
                <h4 className="text-lg font-medium mb-4">Share this Article</h4>
                <div className="flex space-x-3">
                  <Button variant="outline" size="sm">
                    Share on Facebook
                  </Button>
                  <Button variant="outline" size="sm">
                    Share on X
                  </Button>
                  <Button variant="outline" size="sm">
                    Share on LinkedIn
                  </Button>
                </div>
              </div>
            </article>
            
            <div className="w-full lg:w-1/3">
              <BlogSidebar />
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default BlogPostPage;
